// magnetic.directive.ts (Angular 20 + GSAP)
import { isPlatformBrowser } from '@angular/common';
import { afterNextRender, Directive, ElementRef, inject, input, NgZone, OnDestroy, PLATFORM_ID } from '@angular/core';
import { gsap } from 'gsap';

@Directive({
  selector: '[magnetic]',
  standalone: true,
})
export class MagneticDirective implements OnDestroy {
  private el = inject(ElementRef<HTMLElement>);
  private zone = inject(NgZone);

  /** Unit for CSS custom properties --magnet-x and --magnet-y ('%', 'px', etc) */
  unit = input<string>('%');

  /** Pull strength relative to element size (0..1) */
  strength = input<number>(0.3);

  /** Elastic return animation duration (seconds) */
  returnDuration = input<number>(0.8);

  /** Elastic ease (GSAP string) e.g. 'elastic.out(1, 0.4)' */
  returnEase = input<string>('elastic.out(1, 0.4)');

  private rect!: DOMRect;
  private frame = 0;
  private lastX = 0;
  private lastY = 0;
  private active = false;

  // current variable values (percent, numeric)
  private vars = { x: 0, y: 0 };
  private returnTween: gsap.core.Tween | null = null;

  private unlistenMove?: () => void;
  private unlistenEnter?: () => void;
  private unlistenLeave?: () => void;
  private unlistenResize?: () => void;
  private unlistenScroll?: () => void;

  constructor() {
    if (!isPlatformBrowser(inject(PLATFORM_ID))) return;
    afterNextRender(() => {
      this.zone.runOutsideAngular(() => {
        const node = this.el.nativeElement;
        this.unlistenEnter = add(node, 'pointerenter', this.onEnter, { passive: true });
        this.unlistenLeave = add(node, 'pointerleave', this.onLeave, { passive: true });
        this.unlistenResize = add(window, 'resize', () => this.measureSoon(), { passive: true });
        this.unlistenScroll = add(window, 'scroll', () => this.measureSoon(), { passive: true });
      });
    });
  }

  private onEnter = (e: PointerEvent) => {
    if (e.pointerType !== 'mouse' && e.pointerType !== 'pen') return;
    this.active = true;
    this.measureNow();

    // stop any return animation when re-entering
    if (this.returnTween) {
      this.returnTween.kill();
      this.returnTween = null;
    }

    this.unlistenMove = add(this.el.nativeElement, 'pointermove', this.onMove, { passive: true });
  };

  private onLeave = () => {
    this.active = false;
    if (this.unlistenMove) {
      this.unlistenMove();
      this.unlistenMove = undefined;
    }

    // animate back to zero using GSAP
    const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const duration = prefersReduced ? 0.15 : this.returnDuration();
    const ease = prefersReduced ? 'power1.out' : this.returnEase();

    // kill any in-flight tween before starting a new one
    if (this.returnTween) this.returnTween.kill();

    this.zone.runOutsideAngular(() => {
      this.returnTween = gsap.to(this.vars, {
        x: 0,
        y: 0,
        duration,
        ease,
        onUpdate: () => this.applyVars(),
      });
    });
  };

  private onMove = (e: PointerEvent) => {
    if (!this.active) return;

    // cancel elastic return while moving
    if (this.returnTween) {
      this.returnTween.kill();
      this.returnTween = null;
    }

    this.lastX = e.clientX;
    this.lastY = e.clientY;

    if (!this.frame) {
      this.frame = requestAnimationFrame(() => {
        this.frame = 0;
        this.update();
      });
    }
  };

  private update() {
    if (!this.rect) this.measureNow();
    const x = this.lastX - this.rect.left;
    const y = this.lastY - this.rect.top;

    const moveX = (x / this.rect.width - 0.5) * 100 * this.strength();
    const moveY = (y / this.rect.height - 0.5) * 100 * this.strength();

    // set numeric vars, GSAP will tween these on leave
    this.vars.x = moveX;
    this.vars.y = moveY;
    this.applyVars();
  }

  private applyVars() {
    // write CSS custom properties as percentages
    const node = this.el.nativeElement;
    node.style.setProperty('--magnet-x', `${this.vars.x}${this.unit()}`);
    node.style.setProperty('--magnet-y', `${this.vars.y}${this.unit()}`);
  }

  private measureSoon() {
    if (!this.frame) {
      this.frame = requestAnimationFrame(() => {
        this.frame = 0;
        this.measureNow();
      });
    }
  }

  private measureNow() {
    this.rect = this.el.nativeElement.getBoundingClientRect();
  }

  ngOnDestroy() {
    if (this.unlistenMove) this.unlistenMove();
    if (this.unlistenEnter) this.unlistenEnter();
    if (this.unlistenLeave) this.unlistenLeave();
    if (this.unlistenResize) this.unlistenResize();
    if (this.unlistenScroll) this.unlistenScroll();
    if (this.frame) cancelAnimationFrame(this.frame);
    if (this.returnTween) this.returnTween.kill();
  }
}

// helper: accepts narrower handlers and casts internally
function add<T extends EventTarget>(target: T, type: string, handler: (e: any) => void, options?: AddEventListenerOptions) {
  target.addEventListener(type, handler as EventListener, options);
  return () => target.removeEventListener(type, handler as EventListener, options);
}
